import * as React from 'react';
import {
  FaClipboardList,
  FaCubes,
  FaFileAlt,
  // FaFileCsv,
  FaTheaterMasks,
  FaUser,
  FaUsers
  // FaTheaterMasks, FaUsersCog
  // tslint:disable-next-line: no-submodule-imports
} from 'react-icons/fa';
import {
  GiSkills
  // tslint:disable-next-line: no-submodule-imports
} from 'react-icons/gi';
// tslint:disable-next-line: no-submodule-imports
import { MdQuestionAnswer } from 'react-icons/md';

import { DbKey } from '../adl-gen/common/db';
import { AppClient, ApplicationRole } from '../adl-gen/totalinteraction/futurefit/db';
import {
  AdminPageLayout,
  AdminPageLayoutFn,
  AdminPageLayoutParams
} from '../admin/ui/common/page-layout/admin-page-layout';
import { i18next } from '../internationalization/i18next';
import { Route } from '../routing/app-routes';
import { AppState } from "../stores/app-types";
import { LoggedIn, LoggedInSuper } from '../stores/identity-store';
import { LayoutType, MenuLink, PageLayout, PageLayoutFn, PageLayoutParams } from '../ui/common/page-layout/page-layout';
import { SelectOption } from '../ui/widgets/select/select';

export type HeaderLinkFn = (
  state: AppState,
  navigateTo: (r: Route) => void,
  isActive: boolean,
  isDisabled?: boolean
) => MenuLink;

// Admin pages
export type AdminPage = 'REPORTS' | 'ADMIN' | 'ADMIN_CLIENT' | 'ADMIN_USERS' | 'ADMIN_GROUPS' | 'ADMIN_ROLE_BUILDER';

// Superuser pages
export type SuperUserPage =
  | 'SUPER_CLIENTS'
  | 'SUPER_COMPETENCIES_SKILLS'
  | "SUPER_RECOMMENDATIONS"
  | 'SUPER_QUESTIONS';

// All possible pages
export type Page =
  | 'MY_ASSESSMENTS'
  | 'QUIZ'
  | 'QUIZ_RESULTS_PROFILE'
  | 'QUIZ_RESULTS_GUIDANCE'
  | 'QUIZ_RESULTS_RECOMMENDATIONS'
  | 'MANAGER_REPORTS'
  | 'MANAGER_RESULTS_PROFILE'
  | 'MANAGER_RESULTS_GUIDANCE'
  | 'MANAGER_RESULTS_RECOMMENDATIONS'
  | SuperUserPage
  | AdminPage;

// All pages with header links - add exclusions as new generic parameters to the "Exclude" type
export type HeaderPage = Exclude<Page, 'QUIZ'>;

// All Application pages ie. not super-user (though admin can have access)
export type ApplicationPage = Exclude<HeaderPage, SuperUserPage | AdminPage>;

export function defaultHeaderLinks(
  identity: LoggedIn | LoggedInSuper,
  s: AppState,
  navigateTo: (r: Route) => void,
  activePage?: Page
): MenuLink[] {
  const links: MenuLink[] = [];
  // REMOVED FOR SUPERUSER
  if (identity.kind === 'logged-in') {
    // && identity.role !== ApplicationRole.super) {
    links.push(
      APPLICATION_LINK_FACTORY.MY_ASSESSMENTS(s, navigateTo, activePage ? activePage === 'MY_ASSESSMENTS' : false)
    );
    if (identity.isManager) {
      links.push(
        APPLICATION_LINK_FACTORY.MANAGER_REPORTS(s, navigateTo, activePage ? activePage === 'MANAGER_REPORTS' : false)
      );
    }
  }

  if (identity.kind === 'logged-in' && identity.role === ApplicationRole.admin) {
    links.push(ADMIN_LINK_FACTORY.ADMIN(s, navigateTo, activePage ? activePage === 'ADMIN' : false));
  }

  return links;
}

export function defaultSidebarLinks(
  identity: LoggedIn | LoggedInSuper,
  s: AppState,
  navigateTo: (r: Route) => void,
  activePage?: Page,
  isDisabled?: boolean
): MenuLink[] {
  const links: MenuLink[] = [];

  if (identity.kind === 'super-logged-in') {
    links.push(
      SUPERUSER_LINK_FACTORY.SUPER_CLIENTS(s, navigateTo, activePage ? activePage === 'SUPER_CLIENTS' : false)
    );
  } else {
    links.push(ADMIN_LINK_FACTORY.ADMIN_CLIENT(s, navigateTo, activePage ? activePage === 'ADMIN_CLIENT' : false));
  }

  links.push(
    ADMIN_LINK_FACTORY.ADMIN_USERS(
      s,
      navigateTo,
      activePage ? activePage === 'ADMIN_USERS' : false,
      isDisabled === undefined ? false : isDisabled
    )
  );

  links.push(
    ADMIN_LINK_FACTORY.ADMIN_GROUPS(
      s,
      navigateTo,
      activePage ? activePage === 'ADMIN_GROUPS' : false,
      isDisabled === undefined ? false : isDisabled
    )
  );

  if (identity.kind === 'super-logged-in') {
    links.push(
      SUPERUSER_LINK_FACTORY.SUPER_COMPETENCIES_SKILLS(
        s,
        navigateTo,
        activePage ? activePage === 'SUPER_COMPETENCIES_SKILLS' : false
      )
    );
  }

  links.push(
    ADMIN_LINK_FACTORY.ADMIN_ROLE_BUILDER(s, navigateTo, activePage ? activePage === 'ADMIN_ROLE_BUILDER' : false)
  );

  if (identity.kind === 'super-logged-in') {
    links.push(
      SUPERUSER_LINK_FACTORY.SUPER_QUESTIONS(s, navigateTo, activePage ? activePage === 'SUPER_QUESTIONS' : false)
    );
    links.push(
      SUPERUSER_LINK_FACTORY.SUPER_RECOMMENDATIONS(s, navigateTo, activePage ? activePage === 'SUPER_RECOMMENDATIONS' : false)
    );
  }

  links.push(
    ADMIN_LINK_FACTORY.REPORTS(
      s,
      navigateTo,
      activePage ? activePage === 'REPORTS' : false,
      isDisabled === undefined ? false : isDisabled
    )
  );

  return links;
}

export const APPLICATION_LINK_FACTORY: { [key in ApplicationPage]: HeaderLinkFn } = {
  MY_ASSESSMENTS: (_, navigateTo, isActive) => ({
    label: i18next.t('header-links.my-assessments', 'My Assessments'),
    isHighlighted: isActive,
    action: async () => navigateTo({ route: 'my-assessments' })
  }),

  QUIZ_RESULTS_PROFILE: (s, navigateTo, isOpen) => {
    if (s.kind !== 'quiz-results') {
      throw Error("Can't create this header at this route");
    }
    return {
      label: i18next.t('header-links.results-profile', 'Report'),
      isHighlighted: isOpen,
      action: isOpen
        ? undefined
        : async () =>
          navigateTo({
            route: 'quiz-results',
            quizId: s.quizResultsStore.quizId,
            userQuizId: s.quizResultsStore.userQuizId,
            page: 'profile',
            // settingsJson: JSON.stringify({ roles: s.quizResultsStore.rolesSelected })
            settingsJson: JSON.stringify(s.quizResultsStore.getSettings())
          })
    };
  },
  QUIZ_RESULTS_GUIDANCE: (s, navigateTo, isActive) => {
    if (s.kind !== 'quiz-results') {
      throw Error("Can't create this header at this route");
    }
    const guidanceAvailable =
      s.quizResultsStore.guidanceAvailable === undefined ? true : s.quizResultsStore.guidanceAvailable;
    return {
      label: i18next.t('header-links.results-guidance', 'Guidance'),
      isHighlighted: isActive,
      isDisabled: !guidanceAvailable,
      action: isActive
        ? undefined
        : guidanceAvailable
          ? async () =>
            navigateTo({
              route: 'quiz-results',
              quizId: s.quizResultsStore.quizId,
              userQuizId: s.quizResultsStore.userQuizId,
              page: 'guidance',
              settingsJson: JSON.stringify({
                ...s.quizResultsStore.getSettings(),
                guidanceAvailable: s.quizResultsStore.guidanceAvailable
              })
            })
          : undefined
    };
  },
  QUIZ_RESULTS_RECOMMENDATIONS: (s, navigateTo, isActive) => {
    if (s.kind !== 'quiz-results') {
      throw Error("Can't create this header at this route");
    }
    const recommendationsAvailable =
      s.quizResultsStore.recommendationsAvailable === undefined ? true : s.quizResultsStore.recommendationsAvailable;
    return {
      label: i18next.t('header-links.results-recommendations', 'Recommendations'),
      isHighlighted: isActive,
      isDisabled: !recommendationsAvailable,
      action: isActive
        ? undefined
        : recommendationsAvailable
          ? async () =>
            navigateTo({
              route: 'quiz-results',
              quizId: s.quizResultsStore.quizId,
              userQuizId: s.quizResultsStore.userQuizId,
              page: 'recommendations',
              settingsJson: JSON.stringify({
                ...s.quizResultsStore.getSettings(),
                recommendationsAvailable: s.quizResultsStore.recommendationsAvailable
              })
            })
          : undefined
    };
  },
  MANAGER_RESULTS_PROFILE: (s, navigateTo, isOpen) => {
    if (s.kind !== 'manager-quiz-results' || !s.identity.isManager) {
      throw Error("Can't create this header at this route");
    }
    return {
      label: i18next.t('header-links.results-profile', 'Report'),
      isHighlighted: isOpen,
      action: isOpen
        ? undefined
        : async () =>
          navigateTo({
            route: 'manager-quiz-results',
            quizId: s.managerQuizResultsStore.quizId,
            page: 'profile',
            settingsJson: JSON.stringify(s.managerQuizResultsStore.getSettings())
          })
    };
  },
  MANAGER_RESULTS_GUIDANCE: (s, navigateTo, isActive) => {
    if (s.kind !== 'manager-quiz-results' || !s.identity.isManager) {
      throw Error("Can't create this header at this route");
    }
    return {
      label: i18next.t('header-links.results-guidance', 'Guidance'),
      isHighlighted: isActive,
      isDisabled: !s.managerQuizResultsStore.guidanceAvailable,
      action: isActive
        ? undefined
        : s.managerQuizResultsStore.guidanceAvailable
          ? async () =>
            navigateTo({
              route: 'manager-quiz-results',
              quizId: s.managerQuizResultsStore.quizId,
              page: 'guidance',
              settingsJson: JSON.stringify({
                ...s.managerQuizResultsStore.getSettings(),
                guidanceAvailable: s.managerQuizResultsStore.guidanceAvailable
              })
            })
          : undefined
    };
  },
  MANAGER_RESULTS_RECOMMENDATIONS: (s, navigateTo, isActive) => {
    if (s.kind !== 'manager-quiz-results' || !s.identity.isManager) {
      throw Error("Can't create this header at this route");
    }
    return {
      label: i18next.t('header-links.results-recommendations', 'Recommendations'),
      isHighlighted: isActive,
      isDisabled: !s.managerQuizResultsStore.recommendationsAvailable,
      action: isActive
        ? undefined
        : s.managerQuizResultsStore.recommendationsAvailable
          ? async () =>
            navigateTo({
              route: 'manager-quiz-results',
              quizId: s.managerQuizResultsStore.quizId,
              page: 'recommendations',
              settingsJson: JSON.stringify({
                ...s.managerQuizResultsStore.getSettings(),
                recommendationsAvailable: s.managerQuizResultsStore.recommendationsAvailable
              })
            })
          : undefined
    };
  },
  MANAGER_REPORTS: (_, navigateTo, isOpen) => ({
    label: i18next.t('header-links.my-team', 'My Team'),
    isHighlighted: isOpen,
    action: async () => navigateTo({ route: 'manager' })
  })
};

// --- [ TODO ] ---------------------------------------------------------------
// Sidebar links not submenu links
// ----------------------------------------------------------------------------
export const ADMIN_LINK_FACTORY: { [key in AdminPage]: HeaderLinkFn } = {
  // --- [ TODO ] -------------------------------------------------------------
  // 1. If it's superuser, check that the clientId = '@@ADMIN@@'
  // 2. If it's admin user, if relevant, check (or set) the client ID
  // --------------------------------------------------------------------------
  ADMIN: (s: AppState, navigateTo, isActive) => {
    // TODO: Client admin in admin, click on My Teams -> AppState s.kind is set to 'manager'...
    // if (s.kind === "admin") {
    if (
      s.identity.kind === 'super-logged-in' ||
      (s.identity.kind === 'logged-in' && s.identity.role === ApplicationRole.admin)
    ) {
      return {
        label: i18next.t('header-links.administration', 'Administration'),
        isHighlighted: isActive,
        action: isActive
          ? undefined
          : async () =>
            navigateTo({
              route: 'admin',
              page: 'client',
              appClientId: s.identity.kind === 'logged-in' ? s.identity.appUserWithDbId.value.appClientId : undefined
            })
      };
    }
    // }
    throw Error(`Identity ${s.identity.kind} state ${s.kind} Can't create this header at this route`);
  },
  ADMIN_CLIENT: (s, navigateTo, isActive) => {
    if (s.kind === 'admin') {
      if (s.identity.kind === 'super-logged-in' || s.identity.role === ApplicationRole.admin) {
        return {
          label: 'Site Settings',
          isHighlighted: isActive,
          Icon: <FaCubes size={20} />,
          action: isActive
            ? undefined
            : async () =>
              navigateTo({
                route: 'admin',
                page: 'client',
                appClientId:
                  s.identity.kind === 'logged-in' ? s.identity.appUserWithDbId.value.appClientId : s.state.appClientId
              })
        };
      }
    }
    throw Error(`Identity ${s.identity.kind} state ${s.kind} Can't create this header at this route`);
  },

  // ------------------------------------
  // For both superuser and admin client
  // ------------------------------------
  ADMIN_USERS: (s, navigateTo, isActive, isDisabled) => {
    if (s.kind === 'admin') {
      if (s.identity.kind === 'super-logged-in' || s.identity.role === ApplicationRole.admin) {
        // if (s.page === "users") {
        return {
          label: 'Users',
          isHighlighted: isActive,
          Icon: <FaUser size={20} />,
          isDisabled,
          action: isActive
            ? undefined
            : async () =>
              navigateTo({
                route: 'admin',
                page: 'users',
                appClientId:
                  s.identity.kind === 'logged-in' ? s.identity.appUserWithDbId.value.appClientId : s.state.appClientId
              })
        };
        // }
      }
    }
    throw Error(`Identity ${s.identity.kind} state ${s.kind} Can't create this header at this route`);
  },
  ADMIN_GROUPS: (s, navigateTo, isActive, isDisabled) => {
    if (s.kind === 'admin') {
      if (s.identity.kind === 'super-logged-in' || s.identity.role === ApplicationRole.admin) {
        // if (s.page === "users") {
        return {
          label: 'Groups',
          isHighlighted: isActive,
          Icon: <FaUsers size={20} />,
          isDisabled,
          action: isActive
            ? undefined
            : async () =>
              navigateTo({
                route: 'admin',
                page: 'groups',
                appClientId:
                  s.identity.kind === 'logged-in' ? s.identity.appUserWithDbId.value.appClientId : s.state.appClientId
              })
        };
        // }
      }
    }
    throw Error(`Identity ${s.identity.kind} state ${s.kind} Can't create this header at this route`);
  },

  ADMIN_ROLE_BUILDER: (s, navigateTo, isActive) => {
    if (s.kind === 'admin') {
      if (s.identity.kind === 'super-logged-in' || s.identity.role === ApplicationRole.admin) {
        return {
          label: 'Role Builder',
          isHighlighted: isActive,
          Icon: <FaTheaterMasks size={20} />,
          action: isActive
            ? undefined
            : async () =>
              navigateTo({
                route: 'admin',
                page: 'roles',
                appClientId:
                  s.identity.kind === 'logged-in' ? s.identity.appUserWithDbId.value.appClientId : s.state.appClientId
              })
        };
      }
    }
    throw Error(`Identity ${s.identity.kind} state ${s.kind} Can't create this header at this route`);
  },

  REPORTS: (s, navigateTo, isActive) => {
    if (s.kind === 'admin') {
      if (s.identity.kind === 'super-logged-in' || s.identity.role === ApplicationRole.admin) {
        return {
          label: i18next.t('header-links.reports', 'Reports'),
          isHighlighted: isActive,
          Icon: <FaFileAlt size={20} />, // or FaFileCsv
          action: isActive
            ? undefined
            : async () =>
              navigateTo({
                route: 'admin',
                page: 'reports',
                appClientId:
                  s.identity.kind === 'logged-in' ? s.identity.appUserWithDbId.value.appClientId : s.state.appClientId
              })
        };
      }
    }
    throw Error(`Identity ${s.identity.kind} state ${s.kind} Can't create this header at this route`);
  }
};

// --- [ TODO ] ---------------------------------------------------------------
// Sidebar links not submenu links
// ----------------------------------------------------------------------------
export const SUPERUSER_LINK_FACTORY: { [key in SuperUserPage]: HeaderLinkFn } = {
  // --- [ TODO ] -------------------------------------------------------------
  // Check that the clientId = '@@ADMIN@@'
  // --------------------------------------------------------------------------
  SUPER_CLIENTS: (s, navigateTo, isActive) => {
    if (s.kind === 'admin') {
      if (s.identity.kind === 'super-logged-in') {
        return {
          label: 'Clients',
          isHighlighted: isActive,
          Icon: <FaCubes size={20} />,
          action: isActive ? undefined : async () => navigateTo({ route: 'admin', page: 'clients' })
        };
      }
    }
    throw Error(`Identity ${s.identity.kind} state ${s.kind} Can't create this header at this route`);
  },
  SUPER_COMPETENCIES_SKILLS: (s, navigateTo, isActive) => {
    if (s.kind === 'admin') {
      if (s.identity.kind === 'super-logged-in') {
        return {
          label: 'Competencies and Skills',
          isHighlighted: isActive,
          Icon: <GiSkills size={20} />,
          action: isActive ? undefined : async () => navigateTo({ route: 'admin', page: 'competencies-skills' })
        };
      }
    }
    throw Error(`Identity ${s.identity.kind} state ${s.kind} Can't create this header at this route`);
  },
  SUPER_RECOMMENDATIONS: (s, navigateTo, isActive) => {
    if (s.kind === 'admin') {
      if (s.identity.kind === 'super-logged-in') {
        return {
          label: "Recommendations",
          isHighlighted: isActive,
          Icon: <FaClipboardList size={20} />,
          action: isActive
            ? undefined
            : async () => navigateTo({ route: "admin", page: "recommendations" })
        };
      }
    }
    throw Error(`Identity ${s.identity.kind} state ${s.kind} Can't create this header at this route`);
  },
  SUPER_QUESTIONS: (s, navigateTo, isActive) => {
    if (s.kind === 'admin') {
      if (s.identity.kind === 'super-logged-in') {
        return {
          label: 'Questions and Answers',
          isHighlighted: isActive,
          Icon: <MdQuestionAnswer size={20} />,
          action: isActive ? undefined : async () => navigateTo({ route: 'admin', page: 'questions' })
        };
      }
    }
    throw Error(`Identity ${s.identity.kind} state ${s.kind} Can't create this header at this route`);
  }
};

export function createPageLayout(params0: {
  logoUrl: string;
  headerLinks?: MenuLink[];
  subLinks?: MenuLink[];
  applicationRole: ApplicationRole;
  appClientOptions: SelectOption[];
  userFullName?: string;
  logout?(): void;
  filterApplied?: boolean;
  categoryFilterApplied?: boolean;
  competencyFilterApplied?: boolean;
  priorityFilterApplied?: boolean;
  roleSelected?: string;
  langSelector?: JSX.Element;
  layoutType?: LayoutType;
}): PageLayoutFn {
  function create(params: PageLayoutParams): JSX.Element {
    let headerLinks: MenuLink[] = [];
    if (params0.headerLinks) {
      headerLinks = headerLinks.concat(params0.headerLinks);
    }
    if (params.headerLinks) {
      headerLinks = headerLinks.concat(params.headerLinks);
    }
    return (
      <PageLayout
        logoUrl={params0.logoUrl}
        userFullName={params0.userFullName}
        logout={params0.logout}
        headerLinks={headerLinks.length > 0 ? headerLinks : undefined}
        subLinks={params0.subLinks}
        filterApplied={params0.filterApplied}
        categoryFilterApplied={params0.categoryFilterApplied}
        competencyFilterApplied={params0.competencyFilterApplied}
        priorityFilterApplied={params0.priorityFilterApplied}
        roleSelected={params0.roleSelected}
        langSelector={params0.langSelector}
        layoutType={params0.layoutType}
        {...params}
      />
    );
  }

  return create;
}

export function createAdminPageLayout(params0: {
  logoUrl: string;
  headerLinks?: MenuLink[];
  subLinks?: MenuLink[];
  sidebarLinks?: MenuLink[];
  sidebarSubMenus?: { [parentLabel: string]: MenuLink[] };
  role: ApplicationRole;
  appClientOptions: SelectOption[];
  userFullName?: string;
  logout?(): void;
  langSelector?: JSX.Element;
  selectedClient?: DbKey<AppClient>;
  onSelectClient?(appClientId: DbKey<AppClient>): void;
}): AdminPageLayoutFn {
  console.debug(
    'DEBUG external createAdminPageLayout appClientOptions',
    params0.appClientOptions.map(opt => opt.label).join(',')
  );
  console.debug('DEBUG external createAdminPageLayout selectedClient', params0.selectedClient);
  function create(params: AdminPageLayoutParams): JSX.Element {
    let sidebarLinks: MenuLink[] = [];
    let headerLinks: MenuLink[] = [];
    let subLinks: MenuLink[] = [];
    const sidebarSubMenus: { [label: string]: MenuLink[] } = {};
    if (params0.headerLinks) {
      headerLinks = headerLinks.concat(params0.headerLinks);
    }
    if (params.headerLinks) {
      headerLinks = headerLinks.concat(params.headerLinks);
    }
    if (params0.subLinks) {
      subLinks = subLinks.concat(params0.subLinks);
    }
    if (params.subLinks) {
      subLinks = subLinks.concat(params.subLinks);
    }
    if (params0.sidebarLinks) {
      sidebarLinks = sidebarLinks.concat(params0.sidebarLinks);
    }
    if (params.sidebarLinks) {
      sidebarLinks = sidebarLinks.concat(params.sidebarLinks);
    }
    if (params0.sidebarSubMenus) {
      for (const label of Object.keys(params0.sidebarSubMenus)) {
        sidebarSubMenus[label] = [];
        sidebarSubMenus[label].concat(params0.sidebarSubMenus[label]);
      }
    }
    if (params.sidebarSubMenus) {
      for (const label of Object.keys(params.sidebarSubMenus)) {
        if (!Object.keys(sidebarSubMenus).includes(label)) {
          sidebarSubMenus[label] = [];
        }
        sidebarSubMenus[label].concat(params.sidebarSubMenus[label]);
      }
    }
    let appClientOptions: SelectOption[] = [];
    if (params0.appClientOptions) {
      appClientOptions = appClientOptions.concat(params0.appClientOptions);
      console.debug(
        'DEBUG create(<AdminPageLayout>) function outer appClientOptions',
        params0.appClientOptions.map(opt => opt.label).join(',')
      );
    }
    if (params.appClientOptions) {
      console.debug(
        'DEBUG create(<AdminPageLayout>) function inner appClientOptions',
        params.appClientOptions.map(opt => opt.label).join(',')
      );
      appClientOptions = appClientOptions.concat(params.appClientOptions);
    }
    return (
      <AdminPageLayout
        {...params}
        {...params0}
        headerLinks={headerLinks}
        subLinks={subLinks}
        sidebarLinks={sidebarLinks}
        sidebarSubMenus={sidebarSubMenus}
      />
    );
  }

  return create;
}



// WEBPACK FOOTER //
// ./src/app/app-pagelayout.tsx